import { Component, Input } from '@angular/core';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { Contact } from '../shared/models/conflict-result';
import { CitizenshipCode, CountryFieldRequirement, RequirementLevel } from '../shared/models/country-field-requirement';

interface RequirementField {
    key: 'nameRequirement' | 'addressRequirement' | 'dateOfBirthRequirement' | 'nationalIdRequirement' | 'phoneRequirement';
    label: string;
}

interface RequirementGroup {
    level: RequirementLevel;
    fields: RequirementField[];
}

@Component({
    selector: 'xp-country-field-requirements',
    templateUrl: './country-field-requirements.component.html',
    styleUrl: './country-field-requirements.component.scss'
})
export class CountryFieldRequirementsComponent {
    @Input() contact!: Contact;
    @Input() config: CountryFieldRequirement | null = null;
    @Input() citizenshipCodes: CitizenshipCode[] = [];

    readonly fields: RequirementField[] = [
        { key: 'nameRequirement', label: 'Name' },
        { key: 'addressRequirement', label: 'Address' },
        { key: 'dateOfBirthRequirement', label: 'Date of Birth' },
        { key: 'nationalIdRequirement', label: 'National ID' },
        { key: 'phoneRequirement', label: 'Phone' },
    ];

    get isAustralia(): boolean {
        return (this.contact?.country ?? '').toUpperCase() === 'AU';
    }

    get showRequirements(): boolean {
        return !this.isAustralia && !this.contact?.isCompany && !!this.config;
    }

    get missingFieldGroups(): RequirementGroup[] {
        if (!this.config) {
            return [];
        }

        const byLevel: Record<RequirementLevel, RequirementField[]> = {
            Required: [],
            Recommended: [],
            Optional: [],
            NotApplicable: [],
        };

        for (const field of this.fields) {
            const level = this.config[field.key];
            if (level === 'NotApplicable' || !this.isFieldEmpty(field.key)) {
                continue;
            }

            byLevel[level].push(field);
        }

        return (['Required', 'Recommended', 'Optional'] as const)
            .map(level => ({ level, fields: byLevel[level] }))
            .filter(group => group.fields.length > 0);
    }

    get hasMissingFields(): boolean {
        return this.missingFieldGroups.length > 0;
    }

    private isFieldEmpty(key: RequirementField['key']): boolean {
        const isBlank = (value: string | null | undefined) => !value || !value.trim();

        switch (key) {
            case 'nameRequirement':
                return isBlank(this.contact.firstName) && isBlank(this.contact.lastName);
            case 'addressRequirement':
                return isBlank(this.contact.street) && isBlank(this.contact.postcode);
            case 'dateOfBirthRequirement':
                return isBlank(this.contact.dateOfBirth);
            case 'phoneRequirement':
                return isBlank(this.contact.phone) && isBlank(this.contact.mobilePhoneNumber);
            case 'nationalIdRequirement':
                return isBlank(this.contact.taxNumber);
        }
    }

    private showAllCitizenshipOptions = false;

    get filteredCitizenshipCodes(): CitizenshipCode[] {
        if (this.showAllCitizenshipOptions) {
            return this.citizenshipCodes;
        }

        const searchText = (this.contact?.citizenship ?? '').trim().toLowerCase();

        if (!searchText) {
            return this.citizenshipCodes;
        }

        return this.citizenshipCodes.filter(code =>
            code.countryName.toLowerCase().includes(searchText) ||
            code.apiInputCode.toLowerCase().includes(searchText)
        );
    }

    onCitizenshipFocus(): void {
        this.showAllCitizenshipOptions = true;
    }

    onCitizenshipInput(): void {
        this.showAllCitizenshipOptions = false;
    }

    get isCitizenshipInvalid(): boolean {
        const value = this.contact.citizenship;
        if (!value || !value.trim()) {
            return false;
        }

        return !this.citizenshipCodes.some(code => code.apiInputCode === value);
    }

    // Mirrors CountryFieldValidationService's blocking conditions, so the "*" only appears when
    // the field is actually mandatory right now, not whenever the country merely supports it.
    get isDrivingLicenceVersionRequired(): boolean {
        const selectedDocumentType = this.contact?.selectedDocuments?.[0]?.selectedOption;
        return !!this.config?.requiresNewZealandVersion && selectedDocumentType === 'DrivingLicence' && !!this.contact?.taxNumber;
    }

    get isNationalIdIssueDateRequired(): boolean {
        const selectedDocumentType = this.contact?.selectedDocuments?.[0]?.selectedOption;
        return !!this.config?.requiresTanzaniaIssueDate && selectedDocumentType === 'NationalId' && !!this.contact?.taxNumber;
    }

    levelClass(level: RequirementLevel): string {
        switch (level) {
            case 'Required':
                return 'requirement-required';
            case 'Recommended':
                return 'requirement-recommended';
            default:
                return 'requirement-optional';
        }
    }

    readonly maxIssueDate: Date = new Date();

    private cachedIssueDateSource: string | null | undefined;
    private cachedIssueDate: Date | null = null;

    get issueDate(): Date | null {
        const source = this.contact.nationalIdIssueDate;
        if (source !== this.cachedIssueDateSource) {
            this.cachedIssueDateSource = source;
            this.cachedIssueDate = source ? new Date(source) : null;
        }

        return this.cachedIssueDate;
    }

    onIssueDateChange(event: MatDatepickerInputEvent<Date>): void {
        const value = event.value;
        this.contact.nationalIdIssueDate = value ? this.formatIsoDate(value) : null;
    }

    private formatIsoDate(date: Date): string {
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        return `${year}-${month}-${day}`;
    }
}
